import { useFetch } from "@/helpers/composables/useFetch";
import { ref, unref, watch, computed, inject } from "vue";
import type { MaybeRef } from "vue";

export function useAddressValidation(
    address: {
        street1: string;
        street2: string;
        city: string;
        state: string;
        postal_code: string;
        country: string;
    },
    isEnabled: MaybeRef<boolean>
) {
    const reactiveAddress = ref(address);
    const isValid = ref(false);
    const isEnabledRef = ref(isEnabled);
    const isLoading = ref(false);
    let route = inject("route");
    const countrySupportsAddressValidation = computed(() => {
        return ["US", "USA", "PR", "PRI"].includes(
            reactiveAddress.value.country
        );
    });

    const validationEnabled = computed(() => {
        return countrySupportsAddressValidation.value && isEnabledRef?.value;
    });

    const isDifferent = ref(false);

    let addressPayload = computed(() => ({
        address: reactiveAddress.value
    }))

    const validAddress = ref({
        street1: "",
        street2: "",
        city: "",
        state: "",
        postal_code: "",
        country: ""
    });
    const { data, canAbort, abort, execute, onFetchError } = useFetch(
        route("endpoint.validateAddress"),
        {
            immediate: Object.keys(address).every(
                (key) => {
                    if(key == 'street2'){
                        // street2 can be empty, so we don't validate it
                        return true; 
                    }
                    const val = unref(address[key]);
                    return val != "" && val != null && val != undefined;
                }),
            beforeFetch(ctx) {
                const { cancel } = ctx;
                isLoading.value = true;
                if (!validationEnabled.value) {
                    cancel();
                }

                ctx.options.headers = {
                    ...ctx.options.headers,
                    'Accept': 'application/json',
                }

                return ctx;
                
            },
            async afterFetch() {
                isLoading.value = false
            },
        }
    )
        .post(addressPayload)
        .json();

    onFetchError((error) => {
        if(error.name !== 'AbortError'){
            isLoading.value = false;
            isValid.value = false;
            isDifferent.value = false;
        }
    });

    function useSuggestedAddress() {
        return validAddress.value;
    }

    watch(
        reactiveAddress,
        (newVal) => {
            
            if (canAbort.value) {
                abort();
            }
            /**
             * check every REQUIRED field on the newVal object for non-empty values
             *
             */

            let addressFields = [ "street1", "city", "state", "postal_code", "country" ];
            let isFullAddress = Object.keys(newVal).every((key) => {
                if(!addressFields.includes(key)) return true;
                return !["", null, undefined].includes(newVal[key]);
            })
            if (
                isFullAddress
            ) {
                execute();
            }else{
                isValid.value = false;
                isDifferent.value = false;
                validAddress.value = {
                    street1: "",
                    street2: "",
                    city: "",
                    state: "",
                    postal_code: "",
                    country: "USA"
                };
            }
        },
        {
            deep: true
        }
    );

    watch(
        ()=> data.value,
        (newVal) => {
            isValid.value = newVal?.data.is_valid;
            isDifferent.value = newVal?.data.is_different 
                && !!newVal?.data.validAddress.street1
                && !!newVal?.data.validAddress.city
                && !!newVal?.data.validAddress.country;
            if (newVal?.data.validAddress) {
                validAddress.value = {
                    ...newVal?.data.validAddress,
                    country: "USA"
                };
            } else {
                validAddress.value = {
                    street1: "",
                    street2: "",
                    city: "",
                    state: "",
                    postal_code: "",
                    country: "USA"
                };
            }
        },
        { deep: true }
    );
    return {
        isValid,
        isLoadingValidation: isLoading,
        reactiveAddress,
        isDifferent,
        validationEnabled,
        validAddress,
        useSuggestedAddress
    };
}
